import * as Sentry from '@sentry/vue'
import router from '@/router'
import { sentryConfig } from '@/config/sentry'
import { useAuth } from '@/composables/useAuth'
import { ErrorUnauthorized, ErrorForbidden, ErrorNotFound } from '@/types/errors'
import {
  buildErrorPageQuery,
  getUserFacingErrorMessage,
  isAxiosNetworkError,
  shouldOfferFullPageRecovery,
} from '@/utils/errorClassifier'
import { errorNotifier } from './errorNotifier'
import { rememberReturnPathBeforeError } from '@/utils/errorReturnPath'
import { isRequestCanceled } from '@/utils/viewRequests'

function rememberPathBeforeErrorPage(): void {
  rememberReturnPathBeforeError(router.currentRoute.value.fullPath)
}

export function goToAppErrorPage(
  query: Record<string, string>,
  mode: 'push' | 'replace' = 'replace',
) {
  rememberPathBeforeErrorPage()
  const loc = { name: 'error' as const, query }
  if (mode === 'replace') {
    void router.replace(loc)
  } else {
    void router.push(loc)
  }
}

/** Full-page error from any thrown / rejected value we can describe safely. */
export function goToErrorPageFromUnknown(error: unknown, mode: 'push' | 'replace' = 'replace') {
  goToAppErrorPage(buildErrorPageQuery(error), mode)
}

export interface ErrorInfo {
  title?: string
  message?: string
  details?: string
  retryFunction?: () => Promise<void> | void
}

export interface ApiError {
  response?: {
    status?: number
    data?: {
      message?: string
    }
  }
  message?: string
}

/**
 * Navigate to the error page with error information
 */
export function navigateToError(errorInfo: ErrorInfo) {
  const query: Record<string, string> = {}
  if (errorInfo.title) query.title = errorInfo.title
  if (errorInfo.message) query.message = errorInfo.message
  if (errorInfo.details) query.details = errorInfo.details

  rememberPathBeforeErrorPage()
  void router.push({
    name: 'error',
    query,
  })
}

/**
 * Handle authentication errors by clearing auth state and redirecting
 */
export function handleAuthError(error?: string) {
  const { clearError, login } = useAuth()

  // Clear any existing error
  clearError()

  // Set the error message
  if (error) {
    // We can't directly set the error on the store from here,
    // so we'll pass it through the router
    navigateToError({
      title: 'Chyba ověření',
      message: error,
      details: 'Zkuste se prosím znovu přihlásit.',
    })
  } else {
    // Redirect to login
    login()
  }
}

/**
 * Handle store-level errors with Sentry logging
 */
export function handleStoreError(
  error: Error,
  context?: {
    errorType?: string
    tags?: Record<string, string>
    redirectTo?: string
  },
): void {
  if (isRequestCanceled(error)) {
    return
  }

  // Handle specific error types
  if (error instanceof ErrorUnauthorized) {
    const { handleAuthFailure } = useAuth()
    handleAuthFailure()
    return
  }

  if (isAxiosNetworkError(error)) {
    if (sentryConfig.enabled) {
      Sentry.captureException(error, {
        tags: {
          errorType: 'network',
          ...context?.tags,
        },
        extra: {
          storeContext: context?.errorType,
        },
      })
    }
    console.error('Network error:', error, context)
    goToErrorPageFromUnknown(error, 'replace')
    return
  }

  if (error instanceof ErrorForbidden) {
    if (sentryConfig.enabled) {
      Sentry.captureException(error, {
        tags: {
          errorType: context?.errorType || 'forbidden',
          ...context?.tags,
        },
      })
    }
    console.error('Store error:', error, context)
    errorNotifier.error('You do not have permission to access this resource')
    if (context?.redirectTo) {
      void router.push(context.redirectTo)
    }
    return
  }

  if (error instanceof ErrorNotFound) {
    if (sentryConfig.enabled) {
      Sentry.captureException(error, {
        tags: {
          errorType: context?.errorType || 'notFound',
          ...context?.tags,
        },
      })
    }
    console.error('Store error:', error, context)
    errorNotifier.warning('The requested resource was not found')
    return
  }

  // Other errors (e.g. 400, 4xx, 5xx as generic Error, unexpected throws)
  if (shouldOfferFullPageRecovery(error)) {
    if (sentryConfig.enabled) {
      Sentry.captureException(error, {
        tags: {
          errorType: context?.errorType || 'store-full-page',
          ...context?.tags,
        },
        extra: {
          storeContext: context?.errorType,
        },
      })
    }
    console.error('Store error (full page):', error, context)
    goToErrorPageFromUnknown(error, 'replace')
    return
  }

  if (sentryConfig.enabled) {
    Sentry.captureException(error, {
      tags: {
        errorType: context?.errorType || 'store-toast',
        ...context?.tags,
      },
    })
  }
  console.error('Store error:', error, context)
  errorNotifier.error(getUserFacingErrorMessage(error))
}
